import { useState } from "react";
import { useConsent } from "@/shared/lib/consent/useConsent";
import { Button } from "@/shared/components/ui/Button";
import { Container } from "@/shared/components/layout/Container";
import { ConsentManager } from "./ConsentManager";

/**
 * Persistent cookie-consent banner. Per design.md it is the sanctioned
 * exception to the "floating notifications must use Toast" rule — it is global
 * page chrome, not a notification: a fixed bottom bar, glass-surface-strong, at
 * the z-overlay layer (above content + toasts, below the z-modal customize
 * Dialog), animated with a plain CSS transition.
 *
 * Mounted via ConsentRoot at the app root, *outside* the RouterProvider tree,
 * so the policy link uses a plain `<a>` (a full reload is fine for a policy
 * page) rather than react-router's `<Link>`, which would have no router context.
 *
 * Shown until the visitor records any decision; "Customize" opens the
 * per-category modal.
 */
export function ConsentBanner() {
  const { hasDecision, acceptAll, essentialOnly } = useConsent();
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Once a decision exists the banner is gone; the modal stays mountable so a
  // re-open from elsewhere isn't needed here (CookieSettingsButton covers that).
  if (hasDecision) return null;

  return (
    <>
      <div
        role="region"
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-overlay glass-surface-strong"
      >
        <Container
          size="6xl"
          className="flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between"
        >
          <p className="type-body-base text-text-muted">
            We use cookies to run the site and, with your consent, to measure
            usage and improve our marketing. See our{" "}
            <a
              href="/cookie-policy"
              className="text-brand underline-offset-4 hover:underline"
            >
              Cookie Policy
            </a>
            .
          </p>
          {/* DOM order is the desktop left-to-right order (Customize, Essential
              only, Accept all). `flex-col-reverse` on mobile flips it to the
              stacked top-to-bottom order (Accept all, Essential only, Customize)
              with the primary action on top — the §10 mobile button pattern. */}
          <div className="flex flex-col-reverse gap-2 md:flex-row md:flex-wrap md:shrink-0">
            <Button
              variant="ghost"
              className="w-full md:w-auto"
              onClick={() => setCustomizeOpen(true)}
            >
              Customize
            </Button>
            <Button
              variant="secondary"
              className="w-full md:w-auto"
              onClick={essentialOnly}
            >
              Essential only
            </Button>
            <Button className="w-full md:w-auto" onClick={acceptAll}>
              Accept all
            </Button>
          </div>
        </Container>
      </div>

      <ConsentManager open={customizeOpen} onOpenChange={setCustomizeOpen} />
    </>
  );
}
